import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { toast } from 'react-toastify'
import { useParams, useSearchParams } from 'react-router-dom'
import styled from '@emotion/styled'
import { Box, Button, LinearProgress, TextField } from '@mui/material'
import { PrintRounded } from '@mui/icons-material'
import Logo from '../../../../assets/img/dostyq-online.png'
import Breadcrumb from '../../../../components/Breadcrumb'
import { ratingApiSlice } from '../../../../features/rating/ratingSlice'
import { useAppSelector } from '../../../../app/hooks'
import { userInfo } from '../../../../features/user/userSlice'
import { capitalize } from '../../../../utils/functions'
import { ui } from '../../../../utils/uiTokens'

const PRACTICE_LABEL = {
    WITH_CAMERA: 'withCamera',
    WITHOUT_CAMERA: 'withoutCamera',
    ABSENT: 'absent',
}

const StudentReportPage = () => {
    const { formatMessage, locale } = useIntl()
    const { userSubjectEnrollId } = useParams()
    const [searchParams] = useSearchParams()
    const user = useAppSelector(userInfo)

    const monthNumber = Number(searchParams.get('monthNumber')) || 0
    const weekNumber = Number(searchParams.get('weekNumber')) || 0

    const { data: report, isFetching } = ratingApiSlice.useGetWeekReportQuery({
        userSubjectEnrollId: +userSubjectEnrollId,
        monthNumber,
        weekNumber,
    })
    const [saveWeekScore, { isLoading: isSaving }] = ratingApiSlice.useSaveWeekScoreMutation()

    const [zachetInput, setZachetInput] = useState('')
    const [commentInput, setCommentInput] = useState('')

    useEffect(() => {
        setZachetInput(
            report?.weekScore?.zachetPoint != null ? String(report.weekScore.zachetPoint) : '',
        )
        setCommentInput(report?.weekScore?.mentorComment || '')
    }, [report])

    if (isFetching) return <LinearProgress />
    if (!report) return null

    const summary = report.summary
    const score = report.weekScore

    const basePayload = {
        userSubjectEnrollId: +userSubjectEnrollId,
        monthNumber,
        weekNumber,
        zachetPoint: score?.zachetPoint ?? null,
        practiceStatus: score?.practiceStatus || '',
        zachetComment: score?.zachetComment || '',
        mentorComment: score?.mentorComment || '',
    }

    const saveZachet = async () => {
        const trimmed = zachetInput.trim()
        try {
            await saveWeekScore({
                ...basePayload,
                zachetPoint: trimmed === '' ? null : Number(trimmed),
            }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
        } catch (error) {}
    }

    const saveComment = async () => {
        try {
            await saveWeekScore({
                ...basePayload,
                mentorComment: commentInput,
            }).unwrap()
            toast.success(formatMessage({ id: 'saved' }))
        } catch (error) {}
    }
    const videoPoint = summary.videoPoint + summary.conspectPoint
    const period = [
        monthNumber ? `${monthNumber} ${formatMessage({ id: 'month' })}` : '',
        weekNumber ? `${weekNumber} ${formatMessage({ id: 'week' })}` : '',
    ]
        .filter(Boolean)
        .join(' · ')

    const cards = [
        { id: 'video', value: summary.videoPoint, max: summary.videoTotal * 20, color: '#4F72EA' },
        { id: 'test', value: summary.testPoint, max: summary.testTotal * 20, color: '#7B5BE8' },
        { id: 'conspect', value: summary.conspectPoint, max: summary.conspectMax, color: ui.green },
        { id: 'zachet', value: summary.zachetPoint, max: 20, color: '#E9A23B' },
        { id: 'practice', value: summary.practicePoint, max: 20, color: ui.pink },
    ]

    return (
        <Wrapper>
            <Toolbar className="no-print">
                <Breadcrumb links={[formatMessage({ id: 'rating' }), formatMessage({ id: 'weeklyReport' })]} />

                <Button
                    variant="contained"
                    startIcon={<PrintRounded />}
                    onClick={() => window.print()}
                >
                    {formatMessage({ id: 'downloadPdf' })}
                </Button>
            </Toolbar>

            <Sheet id="report-sheet">
                <SheetHeader>
                    <Box>
                        <img
                            src={Logo}
                            alt="Dostyq Online"
                            style={{ width: 150 }}
                        />
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                        <ReportTitle>{formatMessage({ id: 'weeklyReport' })}</ReportTitle>
                        <Pink>{period}</Pink>
                    </Box>
                </SheetHeader>

                <StudentCard>
                    <Avatar>
                        {(summary.firstName?.[0] || '') + (summary.lastName?.[0] || '')}
                    </Avatar>

                    <Box sx={{ flexGrow: 1 }}>
                        <StudentName>
                            {summary.firstName} {summary.lastName}
                        </StudentName>
                        <Muted>
                            {summary.groupTitle}
                            {report[`subjectTitle${capitalize(locale)}`]
                                ? ` · ${report[`subjectTitle${capitalize(locale)}`]}`
                                : ''}
                            {user?.firstName ? ` · ${user.firstName} ${user.lastName}` : ''}
                        </Muted>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                        <Muted>{formatMessage({ id: 'weekTotal' })}</Muted>
                        <TotalValue>
                            {summary.totalPoint}
                            <small> / {summary.maxPoint}</small>
                        </TotalValue>
                        {report.rank > 0 && (
                            <Green>{formatMessage({ id: 'placeInGroup' }, { rank: report.rank })}</Green>
                        )}
                    </Box>
                </StudentCard>

                <Cards>
                    {cards.map((card) => (
                        <MetricCard
                            key={card.id}
                            color={card.color}
                        >
                            <Muted>{formatMessage({ id: card.id })}</Muted>
                            <MetricValue>
                                {card.value}
                                <small> / {card.max}</small>
                            </MetricValue>
                        </MetricCard>
                    ))}
                </Cards>

                <SectionTitle color="#4F72EA">
                    1. {formatMessage({ id: 'videoLessonsAndConspect' })}
                    <Badge>{formatMessage({ id: 'automatic' })}</Badge>
                </SectionTitle>

                <ReportTable>
                    <thead>
                        <tr>
                            <th>{formatMessage({ id: 'topic' })}</th>
                            <th>{formatMessage({ id: 'watched' })}</th>
                            <th>{formatMessage({ id: 'conspect' })}</th>
                            <th className="right">{formatMessage({ id: 'score' })}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.videoRows.map((row, index) => (
                            <tr key={index}>
                                <td>{row.videoTitle || row.topicTitle}</td>
                                <td>{row.watchedPercent}%</td>
                                <td>{row.conspectPoint ? `+${row.conspectPoint}` : '-'}</td>
                                <td className="right">{row.point}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={3}>{formatMessage({ id: 'totalPoint' })}</td>
                            <td className="right">{videoPoint}</td>
                        </tr>
                    </tfoot>
                </ReportTable>

                <SectionTitle color="#7B5BE8">
                    2. {formatMessage({ id: 'topicTestResults' })}
                    <Badge>{formatMessage({ id: 'automatic' })}</Badge>
                </SectionTitle>

                <ReportTable>
                    <thead>
                        <tr>
                            <th>{formatMessage({ id: 'test' })}</th>
                            <th>{formatMessage({ id: 'correctAnswer' })}</th>
                            <th>%</th>
                            <th className="right">{formatMessage({ id: 'score' })}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {report.testRows.map((row, index) => (
                            <tr key={index}>
                                <td>{row.testTitle}</td>
                                <td>
                                    {row.point} / {row.maxPoint}
                                </td>
                                <td>{row.percent}%</td>
                                <td className="right">{row.ratingPoint}</td>
                            </tr>
                        ))}
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={3}>{formatMessage({ id: 'totalPoint' })}</td>
                            <td className="right">{summary.testPoint}</td>
                        </tr>
                    </tfoot>
                </ReportTable>

                <SectionTitle color={ui.pink}>
                    3. {formatMessage({ id: 'practiceAttendance' })}
                    <Badge>{formatMessage({ id: 'systemAndMentor' })}</Badge>
                </SectionTitle>

                <PlainCard>
                    <Box sx={{ fontSize: '15px', fontWeight: 600, color: ui.ink }}>
                        {score?.practiceStatus
                            ? formatMessage({ id: PRACTICE_LABEL[score.practiceStatus] })
                            : formatMessage({ id: 'notMarked' })}
                    </Box>
                    <Muted>{summary.practicePoint} / 20</Muted>
                </PlainCard>

                <SectionTitle color="#E9A23B">
                    4. {formatMessage({ id: 'zachetGrade' })}
                    <Badge>{formatMessage({ id: 'mentorSets' })}</Badge>
                </SectionTitle>

                <ZachetRow>
                    <ZachetCard>
                        <Muted>{formatMessage({ id: 'zachet' })}</Muted>
                        <MetricValue>
                            {summary.zachetPoint}
                            <small> / 20</small>
                        </MetricValue>
                    </ZachetCard>

                    <PlainCard style={{ flexGrow: 1 }}>
                        <Muted>{formatMessage({ id: 'gradeScale' })}</Muted>
                        <ScaleTrack>
                            <ScaleFill style={{ width: `${(summary.zachetPoint / 20) * 100}%` }} />
                        </ScaleTrack>
                        <ScaleTicks>
                            <span>0</span>
                            <span>5</span>
                            <span>10</span>
                            <span>15</span>
                            <span>20</span>
                        </ScaleTicks>
                        <Comment>{score?.zachetComment || ''}</Comment>

                        <EditRow className="no-print">
                            <TextField
                                type="number"
                                size="small"
                                label={formatMessage({ id: 'zachet' })}
                                value={zachetInput}
                                onChange={(e) => setZachetInput(e.target.value)}
                                inputProps={{ min: 0, max: 20 }}
                                sx={{ width: 130 }}
                            />
                            <Button
                                variant="contained"
                                onClick={saveZachet}
                                disabled={isSaving}
                            >
                                {formatMessage({ id: 'save' })}
                            </Button>
                        </EditRow>
                    </PlainCard>
                </ZachetRow>

                <SectionTitle color="#4F72EA">
                    5. {formatMessage({ id: 'mentorFeedback' })}
                </SectionTitle>

                <FeedbackBox>
                    <Comment className="print-only">{score?.mentorComment || ''}</Comment>
                    <RuledLine className="print-only" />
                    <RuledLine className="print-only" />
                    <RuledLine className="print-only" />

                    <Box className="no-print">
                        <TextField
                            multiline
                            minRows={4}
                            fullWidth
                            placeholder={formatMessage({ id: 'mentorFeedback' })}
                            value={commentInput}
                            onChange={(e) => setCommentInput(e.target.value)}
                        />
                        <EditRow>
                            <Button
                                variant="contained"
                                onClick={saveComment}
                                disabled={isSaving}
                            >
                                {formatMessage({ id: 'save' })}
                            </Button>
                        </EditRow>
                    </Box>
                </FeedbackBox>

                <SignatureRow>
                    <SignatureBox>
                        <Muted>{formatMessage({ id: 'mentorSignature' })}</Muted>
                    </SignatureBox>
                    <SignatureBox>
                        <Muted>{formatMessage({ id: 'reportDate' })}</Muted>
                    </SignatureBox>
                </SignatureRow>

                <SummaryBar>
                    <Box>
                        <SummaryLabel>{formatMessage({ id: 'weekTotal' })}</SummaryLabel>
                        <SummaryDetails>
                            {formatMessage({ id: 'video' })} {videoPoint} ·{' '}
                            {formatMessage({ id: 'test' })} {summary.testPoint} ·{' '}
                            {formatMessage({ id: 'zachet' })} {summary.zachetPoint} ·{' '}
                            {formatMessage({ id: 'practice' })} {summary.practicePoint}
                        </SummaryDetails>
                    </Box>

                    <Box sx={{ textAlign: 'right' }}>
                        <SummaryTotal>
                            {summary.totalPoint}
                            <small> / {summary.maxPoint}</small>
                        </SummaryTotal>
                        {report.rank > 0 && (
                            <Green>{formatMessage({ id: 'placeInGroup' }, { rank: report.rank })}</Green>
                        )}
                    </Box>
                </SummaryBar>

                <Footnote>{formatMessage({ id: 'reportFootnote' })}</Footnote>
            </Sheet>
        </Wrapper>
    )
}

export default StudentReportPage

const Wrapper = styled.div`
    margin: 10px;

    .print-only {
        display: none;
    }

    @media print {
        margin: 0;

        .no-print {
            display: none !important;
        }

        .print-only {
            display: block;
        }
    }
`

const EditRow = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    margin-top: 14px;
`

const Toolbar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
`

const Sheet = styled.div`
    background: #fff;
    border: 1px solid ${ui.border};
    border-radius: 16px;
    padding: 32px;
    max-width: 900px;
    margin: 0 auto;

    @media print {
        border: none;
        border-radius: 0;
        padding: 0;
        max-width: none;
    }
`

const SheetHeader = styled.div`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 16px;
    border-bottom: 3px solid ${ui.pink};
`

const ReportTitle = styled.div`
    font-size: 22px;
    font-weight: 700;
    color: ${ui.ink};
`

const Muted = styled.div`
    font-size: 12px;
    color: ${ui.muted};
`

const Pink = styled.div`
    font-size: 13px;
    font-weight: 600;
    color: ${ui.pink};
`

const Green = styled.div`
    font-size: 12.5px;
    font-weight: 600;
    color: ${ui.green};
`

const StudentCard = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
    margin-top: 20px;
    padding: 20px;
    border-radius: 14px;
    background: #fbf9fb;
`

const Avatar = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    font-weight: 600;
    color: ${ui.pink};
    background: ${ui.pinkSoft};
`

const StudentName = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${ui.ink};
`

const TotalValue = styled.div`
    font-size: 30px;
    font-weight: 700;
    color: ${ui.pink};

    small {
        font-size: 14px;
        color: ${ui.muted};
    }
`

const Cards = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 12px;
    margin: 20px 0 8px;

    @media screen and (max-width: 700px) {
        grid-template-columns: repeat(2, 1fr);
    }
`

const MetricCard = styled.div<{ color: string }>`
    padding: 14px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
    border-top: 3px solid ${({ color }) => color};
`

const MetricValue = styled.div`
    font-size: 20px;
    font-weight: 700;
    color: ${ui.ink};

    small {
        font-size: 12px;
        color: ${ui.muted};
    }
`

const SectionTitle = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 24px 0 12px;
    padding-left: 10px;
    border-left: 4px solid ${({ color }) => color};
    font-size: 15px;
    font-weight: 700;
    color: ${ui.ink};
`

const Badge = styled.span`
    margin-left: auto;
    padding: 4px 10px;
    border-radius: 100px;
    font-size: 10.5px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${ui.sub};
    background: #f5f3f6;
`

const ReportTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        padding: 10px 12px;
        background: #fbf9fb;
        color: ${ui.muted};
        font-weight: 600;
        font-size: 12px;
    }

    td {
        padding: 10px 12px;
        border-top: 1px solid ${ui.border};
        color: ${ui.ink};
    }

    tfoot td {
        font-weight: 700;
        background: #fbf9fb;
    }

    .right {
        text-align: right;
    }
`

const PlainCard = styled.div`
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
`

const ZachetRow = styled.div`
    display: flex;
    gap: 12px;
    align-items: stretch;
    flex-wrap: wrap;
`

const ZachetCard = styled.div`
    min-width: 160px;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid #f3e2c6;
    background: #fdf8ef;
    text-align: center;
`

const ScaleTrack = styled.div`
    height: 10px;
    margin: 10px 0 6px;
    border-radius: 100px;
    background: ${ui.track};
    overflow: hidden;
`

const ScaleFill = styled.div`
    height: 100%;
    border-radius: 100px;
    background: #e9a23b;
`

const ScaleTicks = styled.div`
    display: flex;
    justify-content: space-between;
    font-size: 11px;
    color: ${ui.muted};
`

const Comment = styled.div`
    margin-top: 10px;
    font-size: 13px;
    line-height: 1.6;
    color: ${ui.ink};
    white-space: pre-wrap;
`

const FeedbackBox = styled.div`
    padding: 16px;
    border-radius: 12px;
    border: 1px solid ${ui.border};
`

const RuledLine = styled.div`
    height: 28px;
    border-bottom: 1px solid ${ui.border};
`

const SignatureRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 24px;
    margin-top: 24px;
`

const SignatureBox = styled.div`
    padding-top: 28px;
    border-top: 1px solid ${ui.ink};
`

const SummaryBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-top: 24px;
    padding: 20px;
    border-radius: 14px;
    background: #1c1a23;
    color: #fff;
`

const SummaryLabel = styled.div`
    font-size: 11px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.6);
`

const SummaryDetails = styled.div`
    margin-top: 4px;
    font-size: 14px;
    font-weight: 600;
`

const SummaryTotal = styled.div`
    font-size: 28px;
    font-weight: 700;

    small {
        font-size: 13px;
        color: rgba(255, 255, 255, 0.6);
    }
`

const Footnote = styled.div`
    margin-top: 12px;
    font-size: 11px;
    color: ${ui.muted};
`
